import { Injectable } from '@angular/core';
import { PaginatedResult } from 'src/app/shared/grid/paginate.model';
import { Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { IPaginatedServiceParamters } from '../core/models/ITable';
import { Contract } from '../core/models/Contract';
import { ContractForAdd } from '../core/models/ContractForAdd';
import { ContractDetailsEditDTO  } from '../core/models/ContractDetailsEditDTO';
import { ContractServicesEditDTO } from '../core/models/ContractServicesEditDTO';
import { ContractInventoryEditDTO } from '../core/models/ContractInventoryEditDTO';
import { ContractFormulaUpdateDTO } from '../core/models/ContractFormulaUpdateDTO';

@Injectable({
  providedIn: 'root',
})
export class ContractService {
  isContractAptForRenewal(contract: Contract): boolean {
    throw new Error('Method not implemented.');
  }
  isContractAptToFinish(contract: Contract): boolean {
    throw new Error('Method not implemented.');
  }
  query: string = '';
  personType: string = 'IsTenant';
  pageNumber: number = 1;
  itemsPerPage: number = 50;

  public searchTypes = [
    { value: 'IsTenant', description: 'Inquilino' },
    { value: 'IsOwner', description: 'Propietario' },
  ];

  constructor(private apiService: ApiService) {}

  getAllPaginated(
    paginatedArguments: IPaginatedServiceParamters,
  ): Observable<PaginatedResult<Contract[]>> {
    return this.apiService.getPaginated(
      '/api/Contract/GetAllContracts',
      paginatedArguments,
    );
  }

  getContract(id: number): Observable<Contract> {
    return this.apiService.get(`/api/Contract/${id}/Detail`);
  }

  editContract(data): Observable<ContractForAdd> {
    return this.apiService.put(`/api/Contract`, data);
  }

  editContractDetails(data: ContractDetailsEditDTO): Observable<any> {
    return this.apiService.put(`/api/Contract/details`, data);
  }

  updateContractFormula(data: ContractFormulaUpdateDTO): Observable<any> {
    return this.apiService.put(`/api/Contract/${data.contractId}/formula`, {
      formulaAumento: data.formulaAumento,
      periodicidad: data.periodicidad,
      increaseType: data.increaseType
    });
  }

  updateRelatedPersons(data: {
    contractId: number;
    type: string;
    persons: any[];
  }): Observable<any> {
    // El backend espera un RelatedPersonsUpdateDTO con esta estructura específica
    const requestBody = {
      contractId: data.contractId,
      type: data.type,
      persons: data.persons.map(person => ({
        id: person.id,
        name: this.getPersonName(person),
        document: this.getPersonDocument(person),
        email: this.getPersonEmail(person),
        streetAddress: this.getPersonAddressField(person, 'streetAddress'),
        city: this.getPersonAddressField(person, 'city'),
        state: this.getPersonAddressField(person, 'state'),
        zipCode: this.getPersonAddressField(person, 'zipCode')
      }))
    };
    
    console.log('ContractService - Sending request:', {
      url: `/api/Contract/${data.contractId}/related-persons/${data.type}`,
      method: 'PUT',
      body: requestBody,
      originalPersons: data.persons
    });
    
    return this.apiService.put(`/api/Contract/${data.contractId}/related-persons/${data.type}`, requestBody);
  }

  private getPersonName(person: any): string {
    console.log('ContractService - Getting person name for:', person);
    
    // Intentar múltiples estructuras de datos
    let name = '';
    
    // Estructura 1: person.user.person.fullName (propietarios/inquilinos)
    if (person.user?.person?.fullName) {
      name = person.user.person.fullName;
    }
    // Estructura 2: person.user.person.firstName + lastName
    else if (person.user?.person?.firstName && person.user?.person?.lastName) {
      name = `${person.user.person.firstName} ${person.user.person.lastName}`;
    }
    // Estructura 3: person.user.person.businessName
    else if (person.user?.person?.businessName) {
      name = person.user.person.businessName;
    }
    // Estructura 4: person.person.businessName (garantes)
    else if (person.person?.businessName) {
      name = person.person.businessName;
    }
    // Estructura 5: person.person.firstName + lastName
    else if (person.person?.firstName && person.person?.lastName) {
      name = `${person.person.firstName} ${person.person.lastName}`;
    }
    // Estructura 6: person.person.fullName
    else if (person.person?.fullName) {
      name = person.person.fullName;
    }
    // Estructura 7: campos directos
    else if (person.businessName) {
      name = person.businessName;
    }
    else if (person.firstName && person.lastName) {
      name = `${person.firstName} ${person.lastName}`;
    }
    else if (person.fullName) {
      name = person.fullName;
    }
    else if (person.name) {
      name = person.name;
    }
    
    console.log('ContractService - Extracted name:', name);
    return name || 'Sin nombre';
  }

  private getPersonDocument(person: any): string {
    console.log('ContractService - Getting person document for:', person);
    
    let document = '';
    
    // Múltiples estructuras para obtener el documento
    document = person.user?.person?.documentId || 
               person.person?.documentId || 
               person.documentId || 
               person.user?.person?.document || 
               person.person?.document || 
               person.document || 
               person.user?.person?.dni || 
               person.person?.dni || 
               person.dni || 
               '';
    
    console.log('ContractService - Extracted document:', document);
    return document ? document.toString() : '';
  }

  private getPersonEmail(person: any): string {
    console.log('ContractService - Getting person email for:', person);
    
    let email = '';
    
    // Múltiples estructuras para obtener el email
    email = person.user?.email || 
            person.email || 
            person.user?.person?.email || 
            person.person?.email || 
            '';
    
    console.log('ContractService - Extracted email:', email);
    return email || 'Sin email';
  }
  
  addTenantToContract(contractId, data): Observable<Contract> {
    return this.apiService.put(
      `/api/Contract/${contractId}/EditContractTenant`,
      data,
    );
  }

  getName(id): Observable<any> {
    return this.apiService.get(`/api/Contract/${id}/name`);
  }

  getContractDetail(id): Observable<Contract> {
    return this.apiService.get(`/api/Contract/${id}/detail`);
  }

  addContract(newContract): Observable<any> {
    
    return this.apiService.post('/api/Contract', newContract);
  }

  getAmounts(id): Observable<any> {
    return this.apiService.get(`/api/Contract/${id}/Amounts`);
  }

  getContractClauses(contractId): Observable<any> {
    return this.apiService.get(`/api/Contract/${contractId}/Clauses`);
  }

  addClause(newClause) {
    return this.apiService.post('/api/contract/AddClause', newClause);
  }

  addExtension(contractId, contractExtension) {
    return this.apiService.post(
      `/api/contract/${contractId}/Extension`,
      contractExtension,
    );
  }

  deleteExtension(extensionId) {
    return this.apiService.delete(`/api/contract/Extension/${extensionId}`);
  }

  cancelContract(contractId) {
    return this.apiService.put(`/api/contract/${contractId}/CancelContract`);
  }

  finishContract(contractId) {
    return this.apiService.put(`/api/contract/${contractId}/EndContract`);
  }

  deleteContract(id) {
    return this.apiService.delete(`/api/contract/${id}`);
  }

  /* SEARCH */
  searchTypeAhead(search: string) {
    if (search === '') {
      return of([]);
    }
    return this.search(search);
  }

  search(query) {
    return this.apiService.get(`/api/Contract/search`, query);
  }

  createQuery(queryString, personType): string {
    let query = '';
    if (personType != 'IsAddress') {
      isNaN(Number(queryString))
        ? (query = 'Name=' + queryString)
        : (query = 'DocumentId=' + queryString);
      query = query + `&${personType}=true&`;
      query =
        query +
        `PageNumber=${this.pageNumber}&CreatedOrNot=true&PageSize=${this.itemsPerPage}`;
    } else {
      query = 'Name=' + queryString + `&${personType}=true&`;
      query =
        query +
        `PageNumber=${this.pageNumber}&CreatedOrNot=true&PageSize=${this.itemsPerPage}`;
    }
    return query;
  }

  getAllInventoryItems(propertyId) {
    return this.apiService.get(
      `/api/Contract/${propertyId}/GetAllInventoryItems`,
    );
  }

  getContractMetadata(contractId) {
    return this.apiService.get(
      `/api/contract/${contractId}/RequirementsMetadata`,
    );
  }
  editAmountInFavor(contractId, amount) {
    return this.apiService.put(
      `/api/Contract/EditAmountInFavor/${contractId}`,
      amount,
    );
  }

  getContractFiles(contractId) {
    return this.apiService.get(`/api/Contract/${contractId}/Files/`);
  }

  getContractInstallmentIncreases(contractId) {
    return this.apiService.get(
      `/api/Contract/${contractId}/InstallmentIncreses/`,
    );
  }

  getContractInstallmentSchemas(contractId) {
    return this.apiService.get(
      `/api/Contract/${contractId}/InstallmentSchemas/`,
    );
  }

  editContractIds(contractIds: { contractId: number, folderId?: string, internalId?: string }): Observable<any> {
    return this.apiService.put(`/api/Contract/edit-ids`, contractIds);
  }

  // Nuevos métodos para edición de servicios e inventario
  editContractServices(contractId: number, servicesData: ContractServicesEditDTO): Observable<any> {
    return this.apiService.put(`/api/Contract/${contractId}/services`, servicesData);
  }

  editContractInventory(contractId: number, inventoryData: ContractInventoryEditDTO): Observable<any> {
    return this.apiService.put(`/api/Contract/${contractId}/inventory`, inventoryData);
  }

  editContractInstallments(contractId: number, editData: any): Observable<any> {
    return this.apiService.put(`/api/Contract/${contractId}/installments`, editData);
  } 
  private getPersonAddressField(person: any, field: string): string {
    console.log('ContractService - Getting address field:', field, 'for person:', person);
    
    let value = '';
    
    // Para personas creadas (vienen de los formularios) - buscar directamente en el objeto
    if (person[field] !== undefined && person[field] !== null) {
      value = person[field];
    }
    // Para personas existentes (vienen de la base de datos) - buscar en estructuras anidadas
    else if (person.user?.person?.address?.[field]) {
      value = person.user.person.address[field];
    }
    else if (person.person?.address?.[field]) {
      value = person.person.address[field];
    }
    else if (person.address?.[field]) {
      value = person.address[field];
    }
    
    console.log('ContractService - Extracted address field:', field, '=', value);
    return value || '';
  }
  
  updateContractReceipt(data: { contractId: number, receiptTypeId: number, hasInVoice: boolean, facturacionDelHonorarioATerceros: boolean }): Observable<any> {
    return this.apiService.put(`/api/contract/${data.contractId}/receipt`, data);
  }

  downloadCSV(filters: any): Observable<Blob> {
    return this.apiService.getBlob('/api/Contract/DownloadCSV', filters);
  }

  downloadExcel(filters: any): Observable<Blob> {
    return this.apiService.getBlob('/api/Contract/DownloadExcel', filters);
  }

  downloadInstallmentsCSV(contractId: number): Observable<Blob> {
    return this.apiService.getBlob(`/api/Contract/${contractId}/DownloadInstallmentsCSV`);
  }

  downloadInstallmentsExcel(contractId: number): Observable<Blob> {
    return this.apiService.getBlob(`/api/Contract/${contractId}/DownloadInstallmentsExcel`);
  }

  downloadReturnsCSV(contractId: number): Observable<Blob> {
    return this.apiService.getBlob(`/api/Return/Contract/${contractId}/DownloadReturnsCSV`);
  }

  downloadReturnsExcel(contractId: number): Observable<Blob> {
    return this.apiService.getBlob(`/api/Return/Contract/${contractId}/DownloadReturnsExcel`);
  }

  downloadContractsToDueExport(filters: any, exportType: string = 'excel', scope: string = 'current'): Observable<Blob> {
    const params = {
      ...filters,
      exportType: exportType,
      scope: scope
    };
    return this.apiService.getBlob('/api/Contract/ToDue/Export', params);
  }

  downloadContractsCSV(contractParams: any): Observable<Blob> {
    return this.apiService.getBlob('/api/Contract/DownloadCSV', contractParams);
  }

  downloadContractsExcel(contractParams: any): Observable<Blob> {
    return this.apiService.getBlob('/api/Contract/DownloadExcel', contractParams);
  }
}
